// Insights — the numbers behind the phone's Insights screen and the web's dashboards, in one place so both
// show the same thing. Pure: tasks / sessions / reports in, plain objects out. Dates are local (localDateStr).
import { localDateStr } from './time.js';
import { buildDayReport } from './dayreport.js';
import { resolveRate, resolveBillable } from './hierarchy.js';

const DAY = 86400000;
const H = 3600000;
const addDays = (date, n) => { const d = new Date(`${date}T12:00:00`); d.setDate(d.getDate() + n); return localDateStr(d); };
const daysBack = (now, n) => { const d = new Date(now); d.setDate(d.getDate() - n); return d; };

/** Totals over every task: counts, time, breaks, money, completion rate, efficiency (time not on break). */
export function allTimeStats(tasks = []) {
  const done = tasks.filter((t) => t.status === 'done');
  const totalTime = tasks.reduce((s, t) => s + (t.timeSpent || 0), 0);
  const totalPause = tasks.reduce((s, t) => s + (t.pauseTime || 0), 0);
  return {
    total: tasks.length,
    completed: done.length,
    pending: tasks.filter((t) => t.status === 'todo').length,
    totalTime, totalPause,
    activeTime: totalTime - totalPause,
    totalMoney: done.reduce((s, t) => s + (t.moneyEarned || 0), 0),
    completionRate: tasks.length > 0 ? Math.round((done.length / tasks.length) * 100) : 0,
    efficiency: totalTime > 0 ? Math.round(((totalTime - totalPause) / totalTime) * 100) : 100,
  };
}

/** One day report per day for the last `days` days (the Daily Log's own numbers) → Map date → report. */
export function dayReports({ tasks = [], sessions = [], projects = [], projectIds = null, notes = [], departments = [], days = 35, now = Date.now() }) {
  const m = new Map();
  for (let i = 0; i < days; i++) {
    const ds = localDateStr(daysBack(now, i));
    m.set(ds, buildDayReport({ date: ds, tasks, sessions, projects, projectIds, notes, departments, now }));
  }
  return m;
}

/** A day report in the shape the logs render: touched / done / skipped tasks, time, per-project rows. */
export function daySummary(r) {
  const touched = new Map();
  r.tasks.completed.forEach((x) => touched.set(x.task.id, x.task));
  r.time.tasks.forEach((x) => touched.set(x.taskId, x.task));
  r.tasks.skipped.forEach((t) => touched.set(t.id, t));
  const msOf = new Map(r.time.tasks.map((x) => [x.taskId, x.ms]));
  const doneIds = new Set(r.tasks.completed.map((x) => x.task.id));
  const total = touched.size;
  const done = doneIds.size;
  const projectBreakdown = {};
  const row = (pid) => (projectBreakdown[pid] = projectBreakdown[pid] || { done: 0, total: 0, time: 0 });
  touched.forEach((t) => { if (!t.projectId) return; row(t.projectId).total++; if (doneIds.has(t.id)) row(t.projectId).done++; });
  Object.entries(r.clock.byProject).forEach(([pid, ms]) => { if (pid !== 'none') row(pid).time += ms; });
  return {
    total, done,
    skipped: r.tasks.skipped.length,
    pending: total - done,
    totalTime: r.clock.clockedMs,
    totalPause: r.breaks.ms,
    totalMoney: r.money.earned,
    pct: total > 0 ? Math.round((done / total) * 100) : 0,
    doneTasks: r.tasks.completed.map((x) => ({ task: x.task, ms: msOf.get(x.task.id) || 0 })),
    workedTasks: r.time.tasks.filter((x) => !doneIds.has(x.taskId)).map((x) => ({ task: x.task, ms: x.ms })),
    skippedTasks: r.tasks.skipped.filter((t) => !doneIds.has(t.id)),
    projectBreakdown,
  };
}

/** The last 7 days (oldest first): tasks done / planned and hours clocked, by the day it happened. */
export function weeklyActivity(reports, now = Date.now()) {
  const out = [];
  for (let i = 6; i >= 0; i--) {
    const d = daysBack(now, i);
    const date = localDateStr(d);
    const r = reports.get(date);
    out.push({
      date, label: i === 0 ? 'Today' : d.toLocaleDateString('en-US', { weekday: 'short' }),
      done: r?.tasks.doneCount || 0, total: r?.tasks.plannedCount || 0, time: r?.clock.clockedMs || 0, highlight: i === 0,
    });
  }
  return out;
}

/** Per project: tasks, done, timer time, money earned, billable amount at the resolved rate. Most time first. */
export function projectInsights(tasks = [], projects = [], deptForProject = () => ({}), deptForTask = null) {
  return projects.map((p) => {
    const pt = tasks.filter((t) => t.projectId === p.id);
    const done = pt.filter((t) => t.status === 'done').length;
    const totalTime = pt.reduce((s, t) => s + (t.timeSpent || 0), 0);
    const totalPause = pt.reduce((s, t) => s + (t.pauseTime || 0), 0);
    const money = pt.filter((t) => t.status === 'done').reduce((s, t) => s + (t.moneyEarned || 0), 0);
    const billableMs = Math.max(0, totalTime - totalPause);
    const dept = deptForProject(p.id);
    const rate = resolveRate(p, dept);
    // D5 (department mode): each task's time at its own department's rate / billable default.
    const billableAmount = deptForTask
      ? pt.reduce((sum, t) => { const d = deptForTask(t) || dept; const r = resolveRate(p, d); return sum + (resolveBillable(p, d) && r ? (Math.max(0, (t.timeSpent || 0) - (t.pauseTime || 0)) / H) * r : 0); }, 0)
      : resolveBillable(p, dept) && rate ? (billableMs / H) * rate : 0;
    return { ...p, total: pt.length, done, totalTime, money, billableAmount, percentage: pt.length > 0 ? Math.round((done / pt.length) * 100) : 0 };
  }).sort((a, b) => b.totalTime - a.totalTime);
}

/** Tasks per priority, and how many of each are done. */
export function priorityStats(tasks = []) {
  const counts = { critical: 0, high: 0, medium: 0, low: 0 };
  const done = { critical: 0, high: 0, medium: 0, low: 0 };
  tasks.forEach((t) => { if (counts[t.priority] !== undefined) { counts[t.priority]++; if (t.status === 'done') done[t.priority]++; } });
  return { counts, done };
}

/** Days in a row (up to today, today may still be empty) with at least one task finished. */
export function completionStreak(tasks = [], now = Date.now()) {
  const doneDays = new Set(tasks.filter((t) => t.status === 'done' && t.completedAt).map((t) => localDateStr(new Date(t.completedAt))));
  let count = 0;
  for (let i = 0; i < 60; i++) {
    if (doneDays.has(localDateStr(daysBack(now, i)))) count++;
    else if (i > 0) break;
  }
  return count;
}

export const topTimeTasks = (tasks = [], n = 5) => tasks.filter((t) => t.timeSpent > 0).sort((a, b) => b.timeSpent - a.timeSpent).slice(0, n);

/** The last `days` days that had anything (today always), newest first, as daySummary rows. */
export function dailyLogs(reports, now = Date.now(), days = 14) {
  const out = [];
  for (let i = 0; i < days; i++) {
    const d = daysBack(now, i);
    const date = localDateStr(d);
    const r = reports.get(date);
    if (!r) continue;
    const day = daySummary(r);
    if (!day.total && !day.totalTime && i > 0) continue;
    out.push({ date, label: i === 0 ? 'Today' : i === 1 ? 'Yesterday' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }), ...day });
  }
  return out;
}

/** The last `weeks` weeks (Sunday → Saturday) added up from the day reports, newest first. */
export function weeklyLogs(reports, now = Date.now(), weeks = 4) {
  const out = [];
  const today = localDateStr(now);
  const start0 = new Date(now); start0.setDate(start0.getDate() - start0.getDay()); start0.setHours(0, 0, 0, 0);
  for (let w = 0; w < weeks; w++) {
    const weekStart = new Date(start0); weekStart.setDate(weekStart.getDate() - w * 7);
    const weekEnd = new Date(weekStart); weekEnd.setDate(weekEnd.getDate() + 6);
    const week = { total: 0, done: 0, totalTime: 0, totalPause: 0, totalMoney: 0, days: [] };
    for (let d = 0; d < 7; d++) {
      const day = new Date(weekStart); day.setDate(day.getDate() + d);
      const ds = localDateStr(day);
      const r = ds <= today ? reports.get(ds) : null;
      if (!r) { week.days.push(0); continue; }
      const x = daySummary(r);
      week.total += x.total; week.done += x.done; week.totalTime += x.totalTime; week.totalPause += x.totalPause; week.totalMoney += x.totalMoney;
      week.days.push(x.done);
    }
    if (!week.total && !week.totalTime && w > 0) continue;
    out.push({
      label: w === 0 ? 'This Week' : w === 1 ? 'Last Week' : `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
      ...week, pct: week.total > 0 ? Math.round((week.done / week.total) * 100) : 0,
    });
  }
  return out;
}

// ---- the desk's dashboards ----------------------------------------------------------------

const isWork = (s) => s && !s.overlay && s.type !== 'travel' && Number.isFinite(s.startedAt);
const endOf = (s, now) => s.endedAt || (s.duration ? s.startedAt + s.duration : now);

/**
 * When the work happens: clocked ms per weekday (0 = Sunday) × hour (0–23), from work sessions in
 * [from, to) (ms). Sessions are split across the hours (and midnights) they cover.
 */
export function hourHeatmap(sessions = [], { from = 0, to = Date.now(), now = Date.now() } = {}) {
  const grid = Array.from({ length: 7 }, () => new Array(24).fill(0));
  let total = 0;
  for (const s of sessions) {
    if (!isWork(s)) continue;
    let a = Math.max(s.startedAt, from);
    const b = Math.min(endOf(s, now), to);
    while (a < b) {
      const d = new Date(a);
      const next = new Date(d); next.setMinutes(0, 0, 0); next.setHours(d.getHours() + 1);
      const slice = Math.min(b, next.getTime()) - a;
      grid[d.getDay()][d.getHours()] += slice; total += slice;
      a += slice;
    }
  }
  let max = 0;
  for (const row of grid) for (const v of row) if (v > max) max = v;
  return { grid, total, max };
}

/** Sunday-start week key of a local date: the Sunday's YYYY-MM-DD. */
export const sundayOf = (date) => { const d = new Date(`${date}T12:00:00`); d.setDate(d.getDate() - d.getDay()); return localDateStr(d); };

/**
 * Clocked hours per week for the last `weeks` weeks (oldest first), split by project and by department:
 * [{ week (Sunday), totalMs, byProject: { pid|'none': ms }, byDept: { id: ms } }].
 */
export function hoursByWeek({ sessions = [], weeks = 12, now = Date.now(), deptOf = () => null } = {}) {
  const today = localDateStr(now);
  const first = addDays(sundayOf(today), -7 * (weeks - 1));
  const rows = new Map();
  for (let i = 0; i < weeks; i++) { const w = addDays(first, i * 7); rows.set(w, { week: w, totalMs: 0, byProject: {}, byDept: {} }); }
  for (const s of sessions) {
    if (!isWork(s)) continue;
    const date = s.date || localDateStr(s.startedAt);
    const row = rows.get(sundayOf(date));
    if (!row) continue;
    const ms = Math.max(0, endOf(s, now) - s.startedAt);
    const pid = s.projectId || 'none';
    const dep = s.departmentId || deptOf(s.projectId) || 'none';
    row.totalMs += ms;
    row.byProject[pid] = (row.byProject[pid] || 0) + ms;
    row.byDept[dep] = (row.byDept[dep] || 0) + ms;
  }
  return [...rows.values()];
}

/**
 * Did the day go to plan? From day reports, oldest first: planned (dated that day), done of those, done in all,
 * carried over, completion rate.
 */
export function planAdherence(reports) {
  return [...reports.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([date, r]) => {
    const planned = r.tasks.plannedCount || 0;
    const plannedIds = new Set((r.tasks.planned || []).map((t) => t.id || t.task?.id));
    const doneOfPlan = r.tasks.completed.filter((x) => plannedIds.has(x.task.id)).length;
    return {
      date, planned, doneOfPlan, done: r.tasks.doneCount || 0,
      carried: (r.tasks.carriedOver || []).length,
      rate: planned ? Math.round((doneOfPlan / planned) * 100) : null,
      clockedMs: r.clock.clockedMs,
    };
  });
}

/**
 * How the day moves between tasks (the taskFlow log the phone writes on every card switch / skip):
 * per day switches, skips, median dwell before leaving a card; and the tasks left most often unfinished.
 */
export function flowStats(flow = [], { from = null, tasksById = new Map() } = {}) {
  const rows = (flow || []).filter((f) => f && (!from || (f.date || '') >= from));
  const days = new Map();
  const bounced = new Map();
  for (const f of rows) {
    const d = days.get(f.date) || { date: f.date, switches: 0, skips: 0, dwell: [] };
    if (f.kind === 'skip') d.skips++; else d.switches++;
    if (Number.isFinite(f.fromActiveMs) && f.fromActiveMs > 0) d.dwell.push(f.fromActiveMs);
    days.set(f.date, d);
    if (f.fromTaskId && f.fromStatus !== 'done') bounced.set(f.fromTaskId, (bounced.get(f.fromTaskId) || 0) + 1);
  }
  const median = (a) => { if (!a.length) return 0; const s = [...a].sort((x, y) => x - y); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
  const byDay = [...days.values()].sort((a, b) => a.date.localeCompare(b.date))
    .map((d) => ({ date: d.date, switches: d.switches, skips: d.skips, medianDwellMs: median(d.dwell) }));
  const all = rows.map((f) => f.fromActiveMs).filter((x) => Number.isFinite(x) && x > 0);
  return {
    byDay,
    switches: rows.filter((f) => f.kind !== 'skip').length,
    skips: rows.filter((f) => f.kind === 'skip').length,
    medianDwellMs: median(all),
    bounced: [...bounced.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8)
      .map(([id, n]) => ({ taskId: id, count: n, task: tasksById.get(id) || null })),
  };
}
